import type { PDPProductThumbnail } from "@components/commerce/product/right-panel/buy-card/types";
import type { Product, ProductVariant } from "@lib/product/types";
import { Attributes } from "@lib/enums/ProductAttributes";
import { getBackgroundColorValue, getProductAttributeValue } from "@lib/utils/attributesUtils";
import type { AlgoliaProduct } from "@lib/wrappers/algolia/types";

export const isVariantInStock = (variant?: ProductVariant): boolean =>
    variant?.availability?.some((channel) => channel.isOnStock) ?? false;

export const isAnyVariantsInStock = (variants: ProductVariant[]): boolean =>
    variants.some(isVariantInStock);

export const getProductImageThumbnails = (
    product: Product,
    locale: string
): PDPProductThumbnail => {
    const { attributes, media, variants, name } = product;
    const colorCode = getProductAttributeValue(attributes, Attributes.colorCode);
    const articleNumber = getProductAttributeValue(attributes, Attributes.articleNumber);
    const dominantColor = getProductAttributeValue(attributes, Attributes.dominantColor, locale);
    const pageTitle = getProductAttributeValue(attributes, Attributes.pageTitle, locale);
    const backgroundColor = getBackgroundColorValue(attributes);
    const [firstImage] = media ?? [];

    const available = isAnyVariantsInStock(variants);

    return {
        productName: name,
        articleNumber,
        dominantColor,
        colorCode,
        image: {
            url: firstImage?.url ?? "",
            label: firstImage?.label ?? "",
        },
        available,
        backgroundColor,
        pageTitle,
    };
};

export const getRelatedProductImageThumbnails = (
    product: AlgoliaProduct
): PDPProductThumbnail[] => {
    const siblings =
        product?.productColors?.map((relatedProduct) => ({
            image: {
                url: relatedProduct.image ?? "",
                label: relatedProduct.imageLabel ?? "",
            },
            productName: relatedProduct.name ?? product.name,
            articleNumber: product.articleNumber,
            dominantColor: relatedProduct.dominantColor ?? product.dominantColor,
            colorCode: relatedProduct.colorCode,
            available: relatedProduct.hasStock,
            ...(product.pageTitle && { pageTitle: product.pageTitle }),
        })) ?? [];

    // Include the product's own color so the list is always complete
    const selfIncluded = siblings.some((t) => t.colorCode === product?.colorCode);
    if (!selfIncluded && product?.colorCode) {
        siblings.push({
            image: {
                url: product.image ?? "",
                label: product.imageLabel ?? "",
            },
            productName: product.name,
            articleNumber: product.articleNumber,
            dominantColor: product.dominantColor,
            colorCode: product.colorCode,
            available: product.hasStock,
            ...(product.pageTitle && { pageTitle: product.pageTitle }),
        });
    }

    return siblings;
};

export const calculatePrice = (centAmount: number, fractionDigits: number) => {
    const divisor = Math.pow(10, fractionDigits);
    return (centAmount / divisor).toFixed(fractionDigits);
};
